import React, { useEffect, useMemo, useRef, useState } from "react";

// Helper function - you'll need to import this from your utils
const formatSeconds = (seconds, format) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

/**
 * Multi-Box CaptionsModeViewer
 * Supports up to 4 colored bounding boxes (green, blue, red, yellow)
 * Each box captures its own content and exports are segregated by box
 * 
 * Props:
 *  - urls: string[]                  // signed frame URLs, in order
 *  - results?: {                     // optional backend bundle
 *      ocrResults: Array<{ file?: string, detections: Array<{ text?: string, originalText?: string, vertices: Array<{x:number,y:number}>, confidence?: number }> }>,
 *      maxValues?: { maxTextLength?: number }
 *    }
 *  - ocrResults?: same as results.ocrResults (if you pass results, this is optional)
 */
export default function MultiBoxCaptionsModeViewer({ urls = [], results, ocrResults }) {
  const frames = results?.ocrResults || ocrResults || [];

  // Box color configuration
  const BOX_COLORS = [
    { name: "Green", color: "emerald", border: "border-emerald-500", bg: "bg-emerald-500/5", text: "text-emerald-600" },
    { name: "Blue", color: "blue", border: "border-blue-500", bg: "bg-blue-500/5", text: "text-blue-600" },
    { name: "Red", color: "red", border: "border-red-500", bg: "bg-red-500/5", text: "text-red-600" },
    { name: "Yellow", color: "yellow", border: "border-yellow-500", bg: "bg-yellow-500/5", text: "text-yellow-600" }
  ];

  // -------- Frame selection --------
  const [selectedFrame, setSelectedFrame] = useState(1);
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(1);
  const currentIndex = Math.max(0, Math.min(urls.length - 1, selectedFrame - 1));
  const detections = frames?.[currentIndex]?.detections || [];

  // Filters
  const [charRange, setCharRange] = useState(null);
  const [areaRange, setAreaRange] = useState(null);
  const [confidenceValue, setConfidenceValue] = useState(50);
  const [confidenceThreshold, setConfidenceThreshold] = useState(50);
  
  // -------- Multiple selection boxes --------
  const [boxes, setBoxes] = useState([
    { id: 0, enabled: true, norm: null, color: BOX_COLORS[0] },
    { id: 1, enabled: false, norm: null, color: BOX_COLORS[1] },
    { id: 2, enabled: false, norm: null, color: BOX_COLORS[2] },
    { id: 3, enabled: false, norm: null, color: BOX_COLORS[3] }
  ]);
  const [activeBoxId, setActiveBoxId] = useState(0);
  
  // -------- Image & overlay refs/size --------
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 });
  const [natural, setNatural] = useState({ w: 1, h: 1 });

  // Drag state
  const [drag, setDrag] = useState(null);

  const measure = () => {
    const img = imgRef.current;
    if (!img) return;
    const width = img.clientWidth;
    const height = img.clientHeight;
    setOverlaySize({ width, height });
    setNatural({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });

    // Initialize default selection for enabled boxes without one
    setBoxes(prev => prev.map((box, idx) => {
      if (!box.enabled || box.norm) return box;
      const w = 2 / 3;
      const h = 0.22;
      const x = (1 - w) / 2;
      const y = Math.max(0, 1 - h - 0.03 - (idx * 0.05));
      return { ...box, norm: { x, y, w, h } };
    }));
  };

  useEffect(() => {
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [urls, selectedFrame]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const normToPxLocal = (n) => ({
    left: n.x * overlaySize.width,
    top: n.y * overlaySize.height,
    width: n.w * overlaySize.width,
    height: n.h * overlaySize.height,
  });

  const beginMove = (boxId, e) => {
    const box = boxes.find(b => b.id === boxId);
    if (!box?.norm || !wrapperRef.current) return;
    
    const rect = wrapperRef.current.getBoundingClientRect();
    const selPx = normToPxLocal(box.norm);
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    
    const inside =
      localX >= selPx.left &&
      localX <= selPx.left + selPx.width &&
      localY >= selPx.top &&
      localY <= selPx.top + selPx.height;
    
    if (!inside) return;
    
    const dx = localX - selPx.left;
    const dy = localY - selPx.top;
    
    setActiveBoxId(boxId);
    setDrag({
      type: "move",
      boxId,
      startClient: { x: e.clientX, y: e.clientY },
      startSel: { ...box.norm },
      offset: { dx, dy },
    });
    e.preventDefault();
  };

  const beginResize = (boxId, handle, e) => {
    e.stopPropagation();
    const box = boxes.find(b => b.id === boxId);
    if (!box?.norm) return;
    
    setActiveBoxId(boxId);
    setDrag({
      type: "handle",
      boxId,
      handle,
      startClient: { x: e.clientX, y: e.clientY },
      startSel: { ...box.norm },
    });
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!drag || !wrapperRef.current) return;
      
      const box = boxes.find(b => b.id === drag.boxId);
      if (!box?.norm) return;
      
      const minW = 0.04;
      const minH = 0.05;

      if (drag.type === "move") {
        const rect = wrapperRef.current.getBoundingClientRect();
        const selPx = normToPxLocal(box.norm);
        const newLeftLocal = clamp(
          e.clientX - rect.left - drag.offset.dx,
          0,
          overlaySize.width - selPx.width
        );
        const newTopLocal = clamp(
          e.clientY - rect.top - drag.offset.dy,
          0,
          overlaySize.height - selPx.height
        );
        
        const nn = {
          x: newLeftLocal / overlaySize.width,
          y: newTopLocal / overlaySize.height,
          w: selPx.width / overlaySize.width,
          h: selPx.height / overlaySize.height,
        };
        
        nn.x = clamp(nn.x, 0, 1 - nn.w);
        nn.y = clamp(nn.y, 0, 1 - nn.h);
        
        setBoxes(prev => prev.map(b => 
          b.id === drag.boxId ? { ...b, norm: nn } : b
        ));
      } else if (drag.type === "handle") {
        const absDx = (e.clientX - drag.startClient.x) / overlaySize.width;
        const absDy = (e.clientY - drag.startClient.y) / overlaySize.height;
        
        let { x, y, w, h } = drag.startSel;

        if (drag.handle === "nw") {
          x = clamp(drag.startSel.x + absDx, 0, drag.startSel.x + drag.startSel.w - minW);
          y = clamp(drag.startSel.y + absDy, 0, drag.startSel.y + drag.startSel.h - minH);
          w = clamp(drag.startSel.w - (x - drag.startSel.x), minW, 1 - x);
          h = clamp(drag.startSel.h - (y - drag.startSel.y), minH, 1 - y);
        }
        if (drag.handle === "ne") {
          y = clamp(drag.startSel.y + absDy, 0, drag.startSel.y + drag.startSel.h - minH);
          w = clamp(drag.startSel.w + absDx, minW, 1 - drag.startSel.x);
          h = clamp(drag.startSel.h - (y - drag.startSel.y), minH, 1 - y);
          x = drag.startSel.x;
        }
        if (drag.handle === "se") {
          w = clamp(drag.startSel.w + absDx, minW, 1 - drag.startSel.x);
          h = clamp(drag.startSel.h + absDy, minH, 1 - drag.startSel.y);
          x = drag.startSel.x;
          y = drag.startSel.y;
        }
        if (drag.handle === "sw") {
          x = clamp(drag.startSel.x + absDx, 0, drag.startSel.x + drag.startSel.w - minW);
          w = clamp(drag.startSel.w - (x - drag.startSel.x), minW, 1 - x);
          h = clamp(drag.startSel.h + absDy, minH, 1 - drag.startSel.y);
          y = drag.startSel.y;
        }
        
        setBoxes(prev => prev.map(b => 
          b.id === drag.boxId ? { ...b, norm: { x, y, w, h } } : b
        ));
      }
    };
    
    const onUp = () => setDrag(null);
    
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, boxes, overlaySize]);

  // -------- Geometry helpers --------
  function blockBBoxNat(vertices = []) {
    const xs = vertices.map((p) => p.x);
    const ys = vertices.map((p) => p.y);
    if (!xs.length || !ys.length) return { x: 0, y: 0, w: 0, h: 0 };
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    return { x: minX, y: minY, w: Math.max(0, maxX - minX), h: Math.max(0, maxY - minY) };
  }

  const insideSel = (bbNat, sel) => {
    if (!sel) return false;
    const b = {
      x: bbNat.x / natural.w,
      y: bbNat.y / natural.h,
      w: bbNat.w / natural.w,
      h: bbNat.h / natural.h,
    };
    return (
      b.x >= sel.x &&
      b.y >= sel.y &&
      b.x + b.w <= sel.x + sel.w &&
      b.y + b.h <= sel.y + sel.h
    );
  };

  // -------- FILTERING --------
  const { charMaxGlobal, areaMaxGlobal } = useMemo(() => {
    let cMax = results?.maxValues?.maxTextLength ?? 0;
    let aMax = 0;
    for (const f of frames) {
      for (const d of f?.detections || []) {
        const len = (d?.text ?? d?.originalText ?? "").trim().length;
        if (len > cMax) cMax = len;
        const bb = blockBBoxNat(d?.vertices || []);
        const area = Math.max(0, (bb.w || 0) * (bb.h || 0));
        if (area > aMax) aMax = area;
      }
    }
    return { charMaxGlobal: cMax, areaMaxGlobal: Math.max(aMax, 1) };
  }, [frames, results]);

  useEffect(() => {
    if (!charRange && Number.isFinite(charMaxGlobal)) setCharRange([0, charMaxGlobal]);
    if (!areaRange && Number.isFinite(areaMaxGlobal)) setAreaRange([0, areaMaxGlobal]);
  }, [charMaxGlobal, areaMaxGlobal, charRange, areaRange]);

  const [minChars, maxCharsSel] = charRange || [0, charMaxGlobal || 0];
  const [minArea, maxAreaSel] = areaRange || [0, areaMaxGlobal || 1];

  function passesFilters(det, bbNat) {
    const len = (det?.text ?? det?.originalText ?? "").trim().length;
    let confidence = det?.confidence ?? 100;
    if (confidence < 1) confidence *= 100;
    const area = Math.max(0, (bbNat?.w || 0) * (bbNat?.h || 0));
    return (
      len >= minChars &&
      len <= maxCharsSel &&
      area >= minArea &&
      area <= maxAreaSel &&
      confidence >= confidenceThreshold
    );
  }

  // -------- Outlines for all blocks --------
  const outlines = useMemo(() => {
    const growPx = 7;
    return (detections || []).map((d, i) => {
      const bbN = blockBBoxNat(d.vertices || []);
      let left = (bbN.x / natural.w) * overlaySize.width;
      let top = (bbN.y / natural.h) * overlaySize.height;
      let width = (bbN.w / natural.w) * overlaySize.width;
      let height = (bbN.h / natural.h) * overlaySize.height;

      let gLeft = Math.max(0, left - growPx);
      let gTop = Math.max(0, top - growPx);
      let gW = width + 2 * growPx;
      let gH = height + 2 * growPx;
      if (gLeft + gW > overlaySize.width) gW = overlaySize.width - gLeft;
      if (gTop + gH > overlaySize.height) gH = overlaySize.height - gTop;

      const pass = passesFilters(d, bbN);
      
      // Check which box(es) contain this detection (only enabled boxes)
      const containingBox = boxes.find(box => box.enabled && box.norm && insideSel(bbN, box.norm));

      return { key: i, left: gLeft, top: gTop, width: gW, height: gH, pass, containingBox };
    });
  }, [detections, overlaySize, natural, boxes, minChars, maxCharsSel, minArea, maxAreaSel, confidenceThreshold]);

  // -------- Box management --------
  const toggleBox = (boxId) => {
    setBoxes(prev => prev.map(box => {
      if (box.id !== boxId) return box;
      
      const newEnabled = !box.enabled;
      
      // If enabling and no norm exists, create default position
      if (newEnabled && !box.norm) {
        const w = 2 / 3;
        const h = 0.22;
        const x = (1 - w) / 2;
        const y = Math.max(0, 1 - h - 0.03 - (boxId * 0.05));
        return { ...box, enabled: true, norm: { x, y, w, h } };
      }
      
      return { ...box, enabled: newEnabled };
    }));
    
    // If disabling the active box, switch to another enabled box
    if (activeBoxId === boxId) {
      const nextActive = boxes.find(b => b.id !== boxId && b.enabled);
      if (nextActive) setActiveBoxId(nextActive.id);
    }
  };

  // -------- Build lines for current frame per box --------
  const linesForCurrentByBox = useMemo(() => {
    const result = {};
    
    for (const box of boxes) {
      if (!box.enabled) {
        result[box.id] = [];
        continue;
      }
      
      if (!box.norm) {
        result[box.id] = [];
        continue;
      }
      
      const inside = (detections || []).filter((d) => {
        const bb = blockBBoxNat(d.vertices || []);
        return insideSel(bb, box.norm) && passesFilters(d, bb);
      });
      
      const items = inside
        .map((d) => {
          const bb = blockBBoxNat(d.vertices || []);
          return {
            text: (d.text ?? d.originalText ?? "").trim(),
            x: bb.x + bb.w / 2,
            y: bb.y + bb.h / 2,
            h: bb.h,
          };
        })
        .filter((t) => t.text.length > 0);

      if (!items.length) {
        result[box.id] = [];
        continue;
      }

      const meanH = items.reduce((a, c) => a + c.h, 0) / items.length;
      const yThresh = Math.max(6, meanH * 0.6);

      items.sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));

      const lines = [];
      let cur = [];
      let lastY = null;
      
      for (const it of items) {
        if (lastY === null || Math.abs(it.y - lastY) <= yThresh) {
          cur.push(it);
          lastY = lastY === null ? it.y : lastY * 0.7 + it.y * 0.3;
        } else {
          cur.sort((a, b) => a.x - b.x);
          lines.push(cur.map((t) => t.text).join(" "));
          cur = [it];
          lastY = it.y;
        }
      }
      
      if (cur.length) {
        cur.sort((a, b) => a.x - b.x);
        lines.push(cur.map((t) => t.text).join(" "));
      }
      
      result[box.id] = lines;
    }
    
    return result;
  }, [detections, boxes, minChars, maxCharsSel, minArea, maxAreaSel, confidenceThreshold]);

  // -------- Export functions --------
  function groupLines(dets, sel) {
    const blocks = (dets || []).filter((d) => {
      const bb = blockBBoxNat(d.vertices || []);
      return insideSel(bb, sel) && passesFilters(d, bb);
    });
    
    const items = blocks
      .map((d) => {
        const bb = blockBBoxNat(d.vertices || []);
        return {
          text: (d.text ?? d.originalText ?? "").trim(),
          x: bb.x + bb.w / 2,
          y: bb.y + bb.h / 2,
          h: bb.h,
        };
      })
      .filter((i) => i.text.length > 0);
      
    if (!items.length) return [];

    const meanH = items.reduce((a, c) => a + c.h, 0) / items.length;
    const yThresh = Math.max(6, meanH * 0.6);

    items.sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));
    
    const out = [];
    let cur = [];
    let lastY = null;
    
    for (const it of items) {
      if (lastY === null || Math.abs(it.y - lastY) <= yThresh) {
        cur.push(it);
        lastY = lastY === null ? it.y : lastY * 0.7 + it.y * 0.3;
      } else {
        cur.sort((a, b) => a.x - b.x);
        out.push(cur.map((t) => t.text).join(" "));
        cur = [it];
        lastY = it.y;
      }
    }
    
    if (cur.length) {
      cur.sort((a, b) => a.x - b.x);
      out.push(cur.map((t) => t.text).join(" "));
    }
    
    return out;
  }

  function escapeCsvValue(val) {
    if (val === null || val === undefined) return "";
    let s = String(val);
    if (/^[=+\-@]/.test(s)) s = "'" + s;
    const mustQuote = /[",\r\n]/.test(s) || /^\s|\s$/.test(s);
    if (mustQuote) s = '"' + s.replace(/"/g, '""') + '"';
    return s;
  }
  
  function rowsToCsv(rows) {
    const lines = rows.map((r) => r.map(escapeCsvValue).join(",")).join("\r\n");
    return "\uFEFF" + lines;
  }

  const guessTimeFromUrl = (u) => {
    try {
      const filename = (u || "").split("/").pop() || "";
      const base = filename.split(".")[0];
      const ms = parseInt(base, 10);
      if (!Number.isFinite(ms)) return "";
      const totalSec = Math.max(0, Math.round(ms / 1000));
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${m}:${s.toString().padStart(2, "0")}`;
    } catch {
      return "";
    }
  };

  function exportCSV() {
    const enabledBoxes = boxes.filter(b => b.enabled);
    
    const headers = ["Frame", "Time"];
    enabledBoxes.forEach(box => {
      headers.push(`${box.color.name} Box`);
    });
    
    const rows = [headers];
    
    for (let i = 0; i < frames.length; i++) {
      const dets = frames[i]?.detections || [];
      const time = guessTimeFromUrl(urls[i]);
      const row = [String(i + 1), time];
      
      for (const box of enabledBoxes) {
        if (box.norm) {
          const lines = groupLines(dets, box.norm);
          row.push(lines.join(" | "));
        } else {
          row.push("");
        }
      }
      
      rows.push(row);
    }
    
    const csv = rowsToCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "multi_box_captions.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function exportTXT() {
    const enabledBoxes = boxes.filter(b => b.enabled);
    const out = [];
    
    for (let i = 0; i < frames.length; i++) {
      const dets = frames[i]?.detections || [];
      const time = guessTimeFromUrl(urls[i]);
      
      out.push(`Frame ${i + 1}${time ? ` | Time: ${time}` : ""}`);
      out.push("=" + "=".repeat(40));
      
      for (const box of enabledBoxes) {
        if (box.norm) {
          const lines = groupLines(dets, box.norm);
          out.push(`[${box.color.name} Box]:`);
          if (lines.length) {
            out.push(...lines);
          } else {
            out.push("(no text)");
          }
          out.push("");
        }
      }
      
      out.push("");
    }
    
    const payload = "\uFEFF" + out.join("\r\n");
    const blob = new Blob([payload], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "multi_box_captions.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // -------- Frame navigation --------
  useEffect(() => {
    setSelectedFrameIndex(selectedFrame);
  }, [selectedFrame]);

  useEffect(() => {
    const total = Math.max(1, urls.length);
    setSelectedFrame((f) => Math.min(Math.max(1, f), total));
    setSelectedFrameIndex((i) => Math.min(Math.max(1, i), total));
  }, [urls.length]);

  function handleFrameSelectionChange({ kind, delta = 0, value }) {
    const total = Math.max(1, urls.length);
    const clamp = (v) => Math.min(Math.max(1, v), total);
    const commitTo = (v) => {
      const next = clamp(Number(v));
      setSelectedFrame(next);
      setSelectedFrameIndex(next);
    };

    if (total < 25) {
      if (kind === "button") {
        commitTo(selectedFrame + delta);
      } else {
        commitTo(value != null ? value : selectedFrameIndex);
      }
      return;
    }

    if (kind === "button") {
      commitTo(selectedFrame + delta);
      return;
    }

    if (kind === "change") {
      setSelectedFrameIndex(clamp(Number(value)));
      return;
    }

    if (kind === "commit") {
      commitTo(value != null ? value : selectedFrameIndex);
      return;
    }
  }

  const handleSetConfidenceValue = (e) => {
    setConfidenceValue(Number(e.target.value));
    setConfidenceThreshold(Number(e.target.value));
  };

  // -------- Render --------
  return (
    <div className="w-full max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left: image + overlay */}
      <div className="lg:col-span-2">
        <div className="relative w-full max-h-[60vh] rounded-xl border overflow-hidden">
          <div ref={wrapperRef} className="relative inline-block mx-auto">
            {urls.length ? (
              <img
                ref={imgRef}
                src={urls[currentIndex]}
                alt={`frame ${currentIndex + 1}`}
                className="block max-w-full max-h-[60vh] object-contain select-none"
                onLoad={measure}
                draggable={false}
              />
            ) : (
              <div className="h-[60vh] flex items-center justify-center">No image</div>
            )}

            {/* Block outlines */}
            {outlines.map((o) => {
              const borderClass = o.containingBox && o.pass
                ? `border-2 ${o.containingBox.color.border}`
                : o.pass
                ? "border-2 border-gray-500"
                : "border-2 border-gray-700 border-dashed";
              
              return (
                <div
                  key={o.key}
                  className={`absolute rounded ${borderClass}`}
                  style={{
                    left: o.left,
                    top: o.top,
                    width: o.width,
                    height: o.height,
                  }}
                />
              );
            })}

            {/* Overlay for selection boxes */}
            {overlaySize.width > 0 && overlaySize.height > 0 && (
              <div className="absolute inset-0">
                {boxes.filter(box => box.enabled).map((box) => {
                  if (!box.norm) return null;
                  const selPx = normToPxLocal(box.norm);
                  const isActive = box.id === activeBoxId;
                  
                  return (
                    <div key={box.id}>
                      <div
                        className={`absolute border-2 ${box.color.border} ${box.color.bg} rounded cursor-move ${
                          isActive ? 'z-20' : 'z-10'
                        }`}
                        style={{
                          left: selPx.left,
                          top: selPx.top,
                          width: selPx.width,
                          height: selPx.height,
                          opacity: isActive ? 1 : 0.7,
                        }}
                        onMouseDown={(e) => beginMove(box.id, e)}
                        title={`${box.color.name} box - Drag to move, use corners to resize`}
                      >
                        {/* Corner handles */}
                        {renderHandle("nw", (handle, e) => beginResize(box.id, handle, e), box.color)}
                        {renderHandle("ne", (handle, e) => beginResize(box.id, handle, e), box.color)}
                        {renderHandle("se", (handle, e) => beginResize(box.id, handle, e), box.color)}
                        {renderHandle("sw", (handle, e) => beginResize(box.id, handle, e), box.color)}
                        
                        {/* Box label */}
                        <div className={`absolute -top-6 left-0 text-xs font-bold ${box.color.text} bg-white px-1 rounded`}>
                          {box.color.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row w-full gap-2 md:gap-4 mt-3">
          {/* Frame slider */}
          <div className="flex flex-grow items-center gap-3">
            <button
              className="btn btn-sm"
              onClick={() => handleFrameSelectionChange({ kind: "button", delta: -1 })}
            >
              Prev
            </button>

            <input
              type="range"
              className="range range-primary"
              min={1}
              max={Math.max(1, urls.length)}
              value={selectedFrameIndex}
              onChange={(e) =>
                handleFrameSelectionChange({ kind: "change", value: e.target.value })
              }
              onMouseUp={() => handleFrameSelectionChange({ kind: "commit" })}
              onTouchEnd={() => handleFrameSelectionChange({ kind: "commit" })}
              onBlur={() => handleFrameSelectionChange({ kind: "commit" })}
            />

            <button
              className="btn btn-sm"
              onClick={() => handleFrameSelectionChange({ kind: "button", delta: +1 })}
            >
              Next
            </button>

            <div className="text-sm opacity-70">Frame {selectedFrameIndex} / {urls.length || 0}</div>
          </div>

          {/* Export buttons */}
          <div className="flex items-center gap-2">
            <button className="btn btn-success btn-sm" onClick={exportCSV}>
              Export CSV
            </button>
            <button className="btn btn-outline btn-sm" onClick={exportTXT}>
              Export TXT
            </button>
          </div>
        </div>

        {/* Box management */}
        <div className="mt-3 p-3 rounded-xl border">
          <div className="font-medium mb-2">Selection Boxes</div>
          <div className="flex flex-wrap gap-2">
            {boxes.map((box) => (
              <div
                key={box.id}
                className={`flex items-center gap-2 px-3 py-1 rounded-lg border-2 ${
                  box.enabled && box.id === activeBoxId 
                    ? `${box.color.border} ${box.color.bg}` 
                    : box.enabled
                    ? 'border-gray-400 bg-gray-50'
                    : 'border-gray-200 bg-white'
                } ${box.enabled ? 'cursor-pointer' : ''}`}
                onClick={() => box.enabled && setActiveBoxId(box.id)}
              >
                <input
                  type="checkbox"
                  checked={box.enabled}
                  onChange={() => toggleBox(box.id)}
                  className="checkbox checkbox-sm"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className={`w-4 h-4 rounded ${box.color.bg} ${box.color.border} border-2`} />
                <span className={`text-sm font-medium ${box.enabled ? '' : 'opacity-50'}`}>
                  {box.color.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* FILTERS */}
        <div className="mt-3 p-3 rounded-xl border">
          <div className="font-medium">Text Block Filters</div>

          {/* Character length */}
          <label className="label mt-2">
            <span className="label-text">
              Character length: <b>{minChars}</b> – <b>{maxCharsSel}</b>
              <span className="opacity-60 ml-2">(max {charMaxGlobal})</span>
            </span>
          </label>
          <div className="-space-y-2">
            <input
              type="range"
              className="range range-xs range-primary"
              min={0}
              max={charMaxGlobal || 1}
              value={minChars}
              onChange={(e) => {
                const v = +e.target.value;
                setCharRange(v <= maxCharsSel ? [v, maxCharsSel] : [v, v]);
              }}
            />
            <input
              type="range"
              className="range range-xs range-primary"
              min={0}
              max={charMaxGlobal || 1}
              value={maxCharsSel}
              onChange={(e) => {
                const v = +e.target.value;
                setCharRange(v >= minChars ? [minChars, v] : [v, v]);
              }}
            />
          </div>

          {/* Block area */}
          <label className="label mt-3">
            <span className="label-text">
              Block size (px²): <b>{Math.round(minArea)}</b> – <b>{Math.round(maxAreaSel)}</b>
              <span className="opacity-60 ml-2">(max {Math.round(areaMaxGlobal)})</span>
            </span>
          </label>
          <div className="-space-y-2">
            <input
              type="range"
              className="range range-xs range-secondary"
              min={0}
              max={Math.max(1, Math.round(areaMaxGlobal))}
              value={Math.min(minArea, areaMaxGlobal)}
              onChange={(e) => {
                const v = +e.target.value;
                setAreaRange(v <= maxAreaSel ? [v, maxAreaSel] : [v, v]);
              }}
            />
            <input
              type="range"
              className="range range-xs range-secondary"
              min={0}
              max={Math.max(1, Math.round(areaMaxGlobal))}
              value={Math.min(maxAreaSel, areaMaxGlobal)}
              onChange={(e) => {
                const v = +e.target.value;
                setAreaRange(v >= minArea ? [minArea, v] : [v, v]);
              }}
            />
          </div>

          {/* Confidence threshold */}
          {detections?.length > 0 && detections[0].confidence && (
            <>
              <label className="label mt-3">
                <span className="label-text tooltip tooltip-top" data-tip="Minimum confidence threshold for text detection">
                  Confidence: <span className="opacity-60 ml-2">{Math.round(confidenceValue)}%</span>
                </span>
              </label>
              <div className="-space-y-2">
                <input
                  type="range"
                  className="range range-xs"
                  min={50}
                  max={100}
                  value={confidenceValue}
                  onChange={handleSetConfidenceValue}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right: text display */}
      <div className="lg:col-span-1">
        {/* Current frame text by box */}
        <div className="font-semibold m-2">Selected areas text - current frame</div>
        <div className="rounded-xl border p-3 max-h-[40vh] overflow-auto">
          {boxes.filter(box => box.enabled).map((box) => (
            <div key={box.id} className="mb-3">
              <div className={`font-medium ${box.color.text} mb-1`}>
                {box.color.name} Box:
              </div>
              {!box.norm ? (
                <div className="opacity-60 text-sm ml-2">No selection</div>
              ) : linesForCurrentByBox[box.id]?.length ? (
                <ol className="list-disc pl-5 text-sm space-y-1 ml-2">
                  {linesForCurrentByBox[box.id].map((ln, i) => (
                    <li key={i} className="whitespace-pre-wrap">{ln}</li>
                  ))}
                </ol>
              ) : (
                <div className="opacity-60 text-sm ml-2">No text in this area</div>
              )}
            </div>
          ))}
          {boxes.filter(box => box.enabled).length === 0 && (
            <div className="opacity-60 text-sm">No boxes enabled. Check a box above to enable it.</div>
          )}
        </div>

        {/* All frames summary */}
        <div className="font-semibold m-2 mt-4">Selected areas text - all frames</div>
        <div className="w-full border-2 rounded-lg drop-shadow-lg">
          <p className="text-xl text-center font-bold">Frames & Text by Box</p>
          <div className="max-h-[35vh] overflow-auto">
            <table className="w-full table table-sm">
              <thead className="sticky top-0 bg-base-300">
                <tr>
                  <th className="text-center w-20">Frame</th>
                  <th className="text-center w-16">Time</th>
                  {boxes.filter(box => box.enabled).map((box) => (
                    <th key={box.id} className={`text-left ${box.color.text}`}>
                      {box.color.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {frames.map((frame, frameIndex) => {
                  const ts = urls?.[frameIndex]
                    ? formatSeconds(
                        Number(urls[frameIndex].split("/").pop().split(".")[0]) / 1000,
                        "min:sec"
                      )
                    : "0:00";

                  return (
                    <tr key={frameIndex}>
                      <td className="text-center">
                        <button
                          className="btn btn-xs"
                          onClick={() => setSelectedFrame(frameIndex + 1)}
                        >
                          {frameIndex + 1}
                        </button>
                      </td>
                      <td className="text-center text-xs">{ts}</td>
                      {boxes.filter(box => box.enabled).map((box) => {
                        const dets = frame?.detections || [];
                        const lines = box.norm ? groupLines(dets, box.norm) : [];
                        return (
                          <td key={box.id} className="text-xs">
                            {lines.length ? (
                              <div className="max-w-[150px] truncate" title={lines.join(" ")}>
                                {lines.join(" ")}
                              </div>
                            ) : (
                              <span className="opacity-40">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Corner handles
function renderHandle(pos, beginResize, colorConfig) {
  const size = 12;
  const half = size / 2;
  const style = {
    left: pos.includes("w") ? -half : undefined,
    right: pos.includes("e") ? -half : undefined,
    top: pos.includes("n") ? -half : undefined,
    bottom: pos.includes("s") ? -half : undefined,
    width: size,
    height: size,
    cursor: pos === "nw" || pos === "se" ? "nwse-resize" : "nesw-resize",
  };
  
  return (
    <div
      onMouseDown={(e) => beginResize(pos, e)}
      className={`absolute bg-white border-2 ${colorConfig.border} rounded-full`}
      style={style}
      title="Drag to resize"
    />
  );
}